import { ErrorCodes } from '@ringana/errors';
import { AxiosError, AxiosRequestConfig, AxiosResponse } from 'axios';
import DomEventHelper from '@/helpers/DomEventHelper';
import ApiRoutes from '../api/ApiRoutes';
import { EErrorMessage } from '../common/enums/messages.enum';
import {
  IApiErrorResponseData,
  IApiRequestParams,
  IRequestObject,
  ITranslatable,
} from '../common/interfaces/api.interface';
import { TApiResponse } from '../common/types/api.types';
import ApplicationError from '../errors/application-error';
import FetchError from '../errors/fetch-error';
import ValidationError from '../errors/validation-error';
import ErrorHandlerHelper from './ErrorHandlerHelper';

export default class ApiHelper {
  public static handleKnownApiError(err: AxiosError<{ details?: (string | ITranslatable)[]; message?: string }>, apiRoute: ApiRoutes<unknown, unknown, unknown>): EErrorMessage {
    const response = err.response as AxiosResponse<IApiErrorResponseData>;
    if(!response) {
      throw new FetchError(ErrorCodes.FETCH_NO_RESPONSE, err, apiRoute);
    }

    const errorCode: ErrorCodes = response?.data?.error ?? ErrorCodes.UNKNOWN;
    switch(errorCode) {
      case ErrorCodes.TAX_NUM_VERIFICATION_CAPTCHA_WRONG:
        throw new ApplicationError(EErrorMessage.CAPTCHA_WRONG);
      case ErrorCodes.VALIDATION_FAILED:
      case ErrorCodes.VALIDATION_PARAMETER_FAILED:
        throw new ValidationError(errorCode, err);
      case ErrorCodes.UNAUTHORIZED:
        throw new ApplicationError(EErrorMessage.UNAUTHORIZED, { err, errorCode });
      case ErrorCodes.NOT_FOUND:
        throw new ApplicationError(EErrorMessage.NOT_FOUND, { err, errorCode });
      case ErrorCodes.ROUTE_NOT_FOUND:
        throw new ApplicationError(EErrorMessage.ROUTE_NOT_FOUND, { err, errorCode });
      case ErrorCodes.INFOWORX_RESPONSE_CONFIRM_NEWSLETTER:
        throw new ApplicationError(EErrorMessage.INFOWORX_RESPONSE_CONFIRM_NEWSLETTER, { err, errorCode });
      case ErrorCodes.ANNOUNCEMENT_CALENDAR_DOOR_TOO_EARLY:
        throw new ApplicationError(EErrorMessage.FORBIDDEN, { err, errorCode });
      case ErrorCodes.ANNOUNCEMENT_CALENDAR_DOOR_TOO_LATE:
        throw new ApplicationError(EErrorMessage.GONE, { err, errorCode });
      case ErrorCodes.LIMITING_BOUNCER_ACTIVE:
        DomEventHelper.fireEvent({ name: `rin-limiting-active` });
        DomEventHelper.fireEvent({ name: `rin-limiting-shopping-disallowed` });
        throw new FetchError(errorCode, err, apiRoute);
      default:
        throw new FetchError(errorCode, err, apiRoute);
    }
  }

  public static cleanResponseData<T>(res: TApiResponse<T>): TApiResponse<T> {
    if(!res.data) {
      return res;
    }
    if(typeof res.data === `string`) {
      (res.data as string) = (res.data as string).replace(new RegExp(/<\/?br ?\/?> ?/gi), ``);
      return res;
    }
    for(const key of Object.keys(res.data as Record<string, unknown>)) {
      if(typeof res.data[key as keyof T] === `string`) {
        (res.data[key as keyof T] as unknown as string) = (res.data[key as keyof T] as unknown as string).replace(new RegExp(/<\/?br ?\/?> ?/gi), ``);
      }
    }
    return res;
  }

  public static getRequestConfig(route: ApiRoutes<unknown, unknown, unknown>, object: IRequestObject<unknown, unknown>): AxiosRequestConfig {
    const method = route.getMethod();
    const requestConfig: AxiosRequestConfig = {
      baseURL: route.getBaseUrl(),
      data: method === `get` ? undefined : object.data,
      headers: object.headers,
      method,
      params: method === `get` ? object.data : undefined,
      responseType: object.responseType || `json`,
      url: object.params ? ApiHelper.transformParamsForURL(route.getUrl(), object.params as IApiRequestParams) : route.getUrl(),
      withCredentials: route.getWithCredentials(),
    };
    if(object.formData) {
      requestConfig.headers = { 'Content-Type': `multipart/form-data` };
    }
    return requestConfig;
  }

  public static transformParamsForURL(url: string, params: IApiRequestParams): string {
    let ret = url;

    for(const param of Object.keys(params)) {
      const value = params[param]?.toString();
      if(!ret.includes(`:${param}`)) {
        ErrorHandlerHelper.handleError(`Parameter ":${param}" is NOT part of route "${url}"`, { logToConsole: true });
      }
      ret = ret.replace(RegExp(`:${param}\\b`), value || ``);
    }

    return ret;
  }
}
